import React, { createContext, useEffect, useMemo, useState } from "react";
import { AscPlatformLayoutProps } from "../AscPlatformLayout.types";

const AscPlatformLayoutContext = createContext<{
  props?: Partial<AscPlatformLayoutProps>;
  pushProps?: (key: string, newProps: Partial<AscPlatformLayoutProps>) => void;
  popProps?: (key: string) => void;
  collapseSidebar?: boolean;
  setCollapseSidebar?: React.Dispatch<React.SetStateAction<boolean>>;
  activePathsSideBarItemId?: string | null;
}>({});

interface AscPlatformLayoutProviderProps {
  children: React.ReactNode;
  activePath?: string;
  pathToSidebarIdMap?: {
    sidebarItemId: string;
    path: string;
  }[];
}

interface PropsMappedStackItem {
  key: string;
  props: Partial<AscPlatformLayoutProps>;
}

function AscPlatformLayoutProvider({
  children,
  activePath,
  pathToSidebarIdMap,
}: AscPlatformLayoutProviderProps) {
  const [propsMappedStack, setPropsMappedStack] = useState<
    PropsMappedStackItem[]
  >([]);
  const [collapseSidebar, setCollapseSidebar] = useState<boolean>(false);
  const [activePathsSideBarItemId, setActivePathsSideBarItemId] = useState<
    string | null
  >();
  const props = useMemo(() => {
    return propsMappedStack.reduce((acc, item) => {
      return {
        ...acc,
        ...item.props,
      };
    }, {});
  }, [propsMappedStack]);

  function pushProps(key: string, newProps: Partial<AscPlatformLayoutProps>) {
    setPropsMappedStack((prev) => {
      const index = prev?.findIndex((item) => item.key === key);

      if (index === -1) {
        return [
          ...prev,
          {
            key,
            props: newProps,
          },
        ];
      }

      prev[index] = {
        key,
        props: newProps,
      };

      return prev;
    });
  }

  function popProps(key: string) {
    setPropsMappedStack((prev) => prev.filter((item) => item.key !== key));
  }

  useEffect(() => {
    if (!activePath) {
      return;
    }
    if (activePath === "/") {
      setActivePathsSideBarItemId("dashboard");
      return;
    }
    const activeItem = pathToSidebarIdMap?.find(
      (item) => activePath?.startsWith(item?.path) && item?.path !== "/"
    );
    setActivePathsSideBarItemId(activeItem?.sidebarItemId || null);
  }, [activePath, pathToSidebarIdMap]);

  return (
    <AscPlatformLayoutContext.Provider
      value={{
        props,
        pushProps,
        popProps,
        collapseSidebar,
        setCollapseSidebar,
        activePathsSideBarItemId,
      }}
    >
      {children}
    </AscPlatformLayoutContext.Provider>
  );
}

export { AscPlatformLayoutContext, AscPlatformLayoutProvider };
